<script setup lang="ts">
import { ref, inject, computed, watch, onMounted, Ref } from 'vue'
import axios from 'axios'
import { toast } from 'vue-sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { GripVertical, Plus, Trash2, Settings, ChevronDown, ChevronRight, Save } from 'lucide-vue-next'

const vhostId = inject<Ref<string> | null>('vhostId', null)

interface MetaField {
	id: number
	label: string
	type: string
	grouping: string
	provider: string
	source: string
	maxsize: number
	answer: string
	position: number
	settings: {
		defaultValue?: string
		options?: string[]
		truevalue?: string
		falsevalue?: string
		calculatedField?: string
		defaultCategories?: string[]
		delim?: string
		requiredTypes?: string
		removeNewLines?: string
	}
	_expanded?: boolean
	_dirty?: boolean
}

interface MetaProvider {
	id: number
	vhost: number
	provider: string
	rank: number
}

const metaFields = ref<MetaField[]>([])
const groupings = ref<string[]>([])
const providers = ref<MetaProvider[]>([])
const selectedGrouping = ref<string>('all')
const selectedProvider = ref<string>('all')
const loading = ref(true)
const saving = ref(false)
const showAddDialog = ref(false)

const fieldTypes = [
	{ value: 'textfield', label: 'Text Field' },
	{ value: 'textarea', label: 'Text Area' },
	{ value: 'dropdown', label: 'Dropdown' },
	{ value: 'multiselect', label: 'Multi-Select' },
	{ value: 'checkbox', label: 'Checkbox' },
	{ value: 'datefield', label: 'Date Field' },
	{ value: 'numeric', label: 'Numeric' },
	{ value: 'freeform', label: 'Freeform' },
]

const newField = ref<Partial<MetaField>>({
	label: '',
	type: 'textfield',
	grouping: '',
	provider: '',
	source: 'manual',
	maxsize: 0,
	settings: { defaultValue: '' },
})

// Fetch metadata config
const fetchMetaConfig = async () => {
	if (!vhostId?.value) return
	loading.value = true
	try {
		const [metaRes, groupRes, provRes] = await Promise.all([
			axios.post('/services/json', { method: 'vhost.getCustomMeta', vhostID: vhostId.value }),
			axios.post('/services/json', { method: 'vhost.getCustomMetaGroupings', vhostID: vhostId.value }),
			axios.post('/services/json', { method: 'vhost.getCustomMetaProviders', vhostID: vhostId.value }),
		])

		if (metaRes.data.status) {
			metaFields.value = (metaRes.data.result || []).map((f: any) => ({
				...f,
				settings: typeof f.settings === 'string' ? JSON.parse(f.settings) : (f.settings || {}),
				_expanded: false,
				_dirty: false,
			}))
		}
		if (groupRes.data.status) {
			groupings.value = groupRes.data.result || []
		}
		if (provRes.data.status) {
			providers.value = provRes.data.result || []
		}
	} catch (error) {
		console.error('Error fetching metadata config:', error)
		toast.error('Failed to load metadata configuration')
	} finally {
		loading.value = false
	}
}

// Filtered fields
const filteredFields = computed(() => {
	return metaFields.value
		.filter(f => {
			if (selectedGrouping.value !== 'all' && f.grouping !== selectedGrouping.value) return false
			if (selectedProvider.value !== 'all' && f.provider !== selectedProvider.value) return false
			return true
		})
		.sort((a, b) => a.position - b.position)
})

// Add a new metadata field
const addMetaField = async () => {
	if (!vhostId?.value || !newField.value.label) return
	try {
		const response = await axios.post('/services/json', {
			method: 'vhost.insertCustomMeta',
			vhostID: vhostId.value,
			label: newField.value.label,
			type: newField.value.type,
			grouping: newField.value.grouping || '',
			provider: newField.value.provider || '',
			maxSize: newField.value.maxsize || 0,
			position: metaFields.value.length,
			settings: JSON.stringify(newField.value.settings || {}),
		})
		if (response.data.status) {
			toast.success('Metadata field added')
			showAddDialog.value = false
			newField.value = { label: '', type: 'textfield', grouping: '', provider: '', source: 'manual', maxsize: 0, settings: { defaultValue: '' } }
			await fetchMetaConfig()
		}
	} catch (error) {
		console.error('Error adding metadata field:', error)
		toast.error('Failed to add metadata field')
	}
}

// Update a metadata field
const updateMetaField = async (field: MetaField) => {
	if (!vhostId?.value) return
	saving.value = true
	try {
		await axios.post('/services/json', {
			method: 'vhost.updateCustomMeta',
			vhostID: vhostId.value,
			metaID: field.id,
			data: {
				label: field.label,
				type: field.type,
				grouping: field.grouping,
				provider: field.provider,
				maxsize: field.maxsize,
				settings: JSON.stringify(field.settings),
			},
		})
		field._dirty = false
		toast.success(`Field "${field.label}" updated`)
	} catch (error) {
		console.error('Error updating metadata field:', error)
		toast.error('Failed to update field')
	} finally {
		saving.value = false
	}
}

// Delete a metadata field
const deleteMetaField = async (field: MetaField) => {
	if (!vhostId?.value) return
	try {
		await axios.post('/services/json', {
			method: 'vhost.deleteCustomMeta',
			vhostID: vhostId.value,
			metaID: field.id,
		})
		toast.success(`Field "${field.label}" deleted`)
		await fetchMetaConfig()
	} catch (error) {
		console.error('Error deleting metadata field:', error)
		toast.error('Failed to delete field')
	}
}

// Add option to a dropdown/multiselect field
const addOption = (field: MetaField) => {
	if (!field.settings.options) field.settings.options = []
	field.settings.options.push('')
	field._dirty = true
}

// Remove option from a dropdown/multiselect field
const removeOption = (field: MetaField, index: number) => {
	field.settings.options?.splice(index, 1)
	field._dirty = true
}

// Toggle advanced settings
const toggleExpanded = (field: MetaField) => {
	field._expanded = !field._expanded
}

// Mark field as dirty when changed
const markDirty = (field: MetaField) => {
	field._dirty = true
}

// Drag and drop
const draggedField = ref<MetaField | null>(null)

const onFieldDragStart = (field: MetaField) => {
	draggedField.value = field
}

const onFieldDrop = async (targetField: MetaField) => {
	if (!draggedField.value || draggedField.value.id === targetField.id) return
	const fromIdx = metaFields.value.findIndex(f => f.id === draggedField.value!.id)
	const toIdx = metaFields.value.findIndex(f => f.id === targetField.id)
	if (fromIdx < 0 || toIdx < 0) return

	const [moved] = metaFields.value.splice(fromIdx, 1)
	metaFields.value.splice(toIdx, 0, moved)

	// Update positions
	metaFields.value.forEach((f, i) => { f.position = i })
	draggedField.value = null
	toast.success('Field order updated')
}

watch(vhostId!, () => {
	fetchMetaConfig()
}, { immediate: true })
</script>

<template>
	<div class="p-4 space-y-4">
		<div class="flex justify-between items-center">
			<div>
				<h2 class="text-2xl font-light">Custom Metadata Configuration</h2>
				<p class="text-sm text-muted-foreground">Configure custom metadata fields for media items.</p>
			</div>
			<Dialog v-model:open="showAddDialog">
				<DialogTrigger as-child>
					<Button>
						<Plus class="h-4 w-4 mr-2" /> Add Field
					</Button>
				</DialogTrigger>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Add Metadata Field</DialogTitle>
					</DialogHeader>
					<div class="space-y-4 py-4">
						<div class="space-y-2">
							<Label>Field Label</Label>
							<Input v-model="newField.label" placeholder="e.g., Copyright, Location" />
						</div>
						<div class="space-y-2">
							<Label>Field Type</Label>
							<Select v-model="newField.type">
								<SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
								<SelectContent>
									<SelectItem v-for="ft in fieldTypes" :key="ft.value" :value="ft.value">{{ ft.label }}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div class="space-y-2">
							<Label>Grouping</Label>
							<Input v-model="newField.grouping" placeholder="Group name (optional)" />
						</div>
						<div class="space-y-2">
							<Label>Provider</Label>
							<Select v-model="newField.provider">
								<SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
								<SelectContent>
									<SelectItem value="">None</SelectItem>
									<SelectItem v-for="p in providers" :key="p.id" :value="p.provider">{{ p.provider }}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						<div class="space-y-2">
							<Label>Default Value</Label>
							<Input v-model="newField.settings!.defaultValue" placeholder="Default value (optional)" />
						</div>
					</div>
					<DialogFooter>
						<Button @click="addMetaField" :disabled="!newField.label">Add Field</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>

		<!-- Filters -->
		<div class="flex space-x-4 items-center">
			<div class="space-y-1">
				<Label class="text-xs">Grouping</Label>
				<Select v-model="selectedGrouping">
					<SelectTrigger class="w-48"><SelectValue /></SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All Groupings</SelectItem>
						<SelectItem value="">No Group</SelectItem>
						<SelectItem v-for="g in groupings" :key="g" :value="g">{{ g }}</SelectItem>
					</SelectContent>
				</Select>
			</div>
			<div class="space-y-1">
				<Label class="text-xs">Provider</Label>
				<Select v-model="selectedProvider">
					<SelectTrigger class="w-48"><SelectValue /></SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All Providers</SelectItem>
						<SelectItem value="">None</SelectItem>
						<SelectItem v-for="p in providers" :key="p.id" :value="p.provider">{{ p.provider }}</SelectItem>
					</SelectContent>
				</Select>
			</div>
			<div class="flex items-end pt-5">
				<Badge variant="secondary">{{ filteredFields.length }} fields</Badge>
			</div>
		</div>

		<!-- Field List -->
		<Card>
			<CardContent class="p-0">
				<div v-if="loading" class="p-8 text-center text-muted-foreground">Loading metadata configuration...</div>
				<div v-else-if="filteredFields.length === 0" class="p-8 text-center text-muted-foreground">
					No metadata fields configured. Click "Add Field" to create one.
				</div>
				<div v-else>
					<div
						v-for="field in filteredFields"
						:key="field.id"
						class="border-b last:border-b-0"
						draggable="true"
						@dragstart="onFieldDragStart(field)"
						@dragover.prevent
						@drop="onFieldDrop(field)"
					>
						<!-- Field Header -->
						<div class="flex items-center justify-between p-3 hover:bg-muted/20">
							<div class="flex items-center space-x-3">
								<GripVertical class="h-4 w-4 text-muted-foreground/50 cursor-grab" />
								<div>
									<div class="flex items-center space-x-2">
										<span class="font-medium text-sm">{{ field.label }}</span>
										<Badge variant="outline" class="text-xs">{{ field.type }}</Badge>
										<Badge v-if="field.grouping" variant="secondary" class="text-xs">{{ field.grouping }}</Badge>
										<Badge v-if="field.provider" class="text-xs">{{ field.provider }}</Badge>
										<Badge v-if="field._dirty" variant="destructive" class="text-xs">unsaved</Badge>
									</div>
									<div class="text-xs text-muted-foreground mt-0.5">
										<span v-if="field.settings.defaultValue">Default: {{ field.settings.defaultValue }}</span>
										<span v-if="field.maxsize > 0" class="ml-2">Max: {{ field.maxsize }}</span>
									</div>
								</div>
							</div>
							<div class="flex items-center space-x-1">
								<Button v-if="field._dirty" variant="outline" size="sm" @click="updateMetaField(field)">
									<Save class="h-3 w-3 mr-1" /> Save
								</Button>
								<Button variant="ghost" size="sm" @click="toggleExpanded(field)">
									<Settings class="h-4 w-4" />
								</Button>
								<Button variant="ghost" size="sm" class="text-destructive" @click="deleteMetaField(field)">
									<Trash2 class="h-4 w-4" />
								</Button>
							</div>
						</div>

						<!-- Expanded Editor -->
						<div v-if="field._expanded" class="p-4 bg-muted/10 border-t space-y-4">
							<div class="grid grid-cols-1 md:grid-cols-3 gap-4">
								<div class="space-y-2">
									<Label class="text-xs">Label</Label>
									<Input v-model="field.label" @input="markDirty(field)" />
								</div>
								<div class="space-y-2">
									<Label class="text-xs">Type</Label>
									<Select v-model="field.type" @update:model-value="markDirty(field)">
										<SelectTrigger><SelectValue /></SelectTrigger>
										<SelectContent>
											<SelectItem v-for="ft in fieldTypes" :key="ft.value" :value="ft.value">{{ ft.label }}</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div class="space-y-2">
									<Label class="text-xs">Grouping</Label>
									<Input v-model="field.grouping" @input="markDirty(field)" />
								</div>
							</div>

							<div class="grid grid-cols-1 md:grid-cols-3 gap-4">
								<div class="space-y-2">
									<Label class="text-xs">Default Value</Label>
									<Input v-model="field.settings.defaultValue" @input="markDirty(field)" />
								</div>
								<div class="space-y-2">
									<Label class="text-xs">Max Size</Label>
									<Input v-model.number="field.maxsize" type="number" @input="markDirty(field)" />
								</div>
								<div class="space-y-2">
									<Label class="text-xs">Required File Types</Label>
									<Input v-model="field.settings.requiredTypes" placeholder="e.g., mp4,mov" @input="markDirty(field)" />
								</div>
							</div>

							<!-- Checkbox specific -->
							<div v-if="field.type === 'checkbox'" class="grid grid-cols-2 gap-4">
								<div class="space-y-2">
									<Label class="text-xs">Checked Value</Label>
									<Input v-model="field.settings.truevalue" @input="markDirty(field)" />
								</div>
								<div class="space-y-2">
									<Label class="text-xs">Unchecked Value</Label>
									<Input v-model="field.settings.falsevalue" @input="markDirty(field)" />
								</div>
							</div>

							<!-- Dropdown/Multiselect options -->
							<div v-if="field.type === 'dropdown' || field.type === 'multiselect'" class="space-y-2">
								<div class="flex justify-between items-center">
									<Label class="text-xs">Options</Label>
									<Button variant="outline" size="sm" @click="addOption(field)">
										<Plus class="h-3 w-3 mr-1" /> Add Option
									</Button>
								</div>
								<div v-for="(opt, idx) in field.settings.options" :key="idx" class="flex items-center space-x-2">
									<Input :model-value="opt" @update:model-value="(v: string | number) => { field.settings.options![idx] = String(v); markDirty(field) }" placeholder="Option value" />
									<Button variant="ghost" size="sm" class="text-destructive" @click="removeOption(field, idx)">
										<Trash2 class="h-3 w-3" />
									</Button>
								</div>
							</div>

							<div class="grid grid-cols-2 gap-4">
								<div class="space-y-2">
									<Label class="text-xs">Delimiter</Label>
									<Input v-model="field.settings.delim" placeholder="e.g., ," @input="markDirty(field)" />
								</div>
								<div class="flex items-center space-x-2 pt-5">
									<Switch
										:checked="field.settings.removeNewLines === 'yes'"
										@update:checked="(v: boolean) => { field.settings.removeNewLines = v ? 'yes' : 'no'; markDirty(field) }"
									/>
									<Label class="text-xs">Remove New Lines</Label>
								</div>
							</div>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	</div>
</template>
